// ---
// What pages exist, and what each one needs
//
// One table. The sidebar reads it to decide what to offer, and the route wrapper reads it to
// decide what to refuse — so a link cannot lead to a page that then says no, and a page the
// caller may use cannot be hidden.
//
// It holds metadata, not paths. `routes.tsx` keeps its literal `createRoute` declarations
// deliberately: the router infers `/tenants/$tenant/users` from them and checks it at every
// `Link` and `navigate`, which a table of strings would throw away.
// ---

/** What the route wrapper hands every page inside a tenant. */
export interface TenantPageProps {
  readonly tenant: string;
  readonly token: string;
  readonly projects: string[];
  readonly scopes: string[];
}

/** A page, named by the last segment of its path. */
export type Page =
  | "applications"
  | "apis"
  | "users"
  | "roles"
  | "projects"
  | "claims"
  | "email"
  | "landing"
  | "logs"
  | "bulk"
  | "users-new"
  | "platform"
  | "tokens";

export interface PageDef {
  readonly page: Page;
  readonly label: string;
  /** The control-plane scope the page needs. Absent means it needs none. */
  readonly scope?: `${string}:${string}`;
  /** Who hands that scope out, for the refusal to say so. */
  readonly grantedBy?: string;
  /**
   * Whether the rail offers it.
   *
   * A page reached from a control on another page — a creation form behind a button — still needs
   * its route gated by the scope it requires, which is what this table is for. It just does not
   * belong in a list of places to go.
   */
  readonly listed?: boolean;
  /**
   * The heading this page sits under in the sidebar.
   *
   * Named rather than positional: "the entry above it" made the parent whatever happened to be
   * declared before, so reordering the table silently reparented a page.
   */
  readonly section?: string;
  /** A place outside any tenant, so its path is whole rather than built from the tenant. */
  readonly href?: string;
}

export interface PageGroup {
  readonly label: string;
  readonly platform?: boolean;
  readonly tenant?: boolean;
  readonly pages: readonly PageDef[];
}

const TENANT_ADMIN = "a tenant administrator role";
/** Who belongs in this tenant, and what they may do. */
export const USER_MANAGEMENT = "User management";
/** What those people see on the way in: the messages they get and the pages they land on. */
export const USER_FLOWS = "User flows";
const TENANT_OR_PROJECT_ADMIN = "a tenant or project administrator role";

export const GROUPS: readonly PageGroup[] = [
  {
    label: "Across tenants",
    platform: true,
    pages: [{ page: "platform", label: "Tenants", href: "/platform" }],
  },
  {
    label: "Tenant",
    tenant: true,
    pages: [
      { page: "apis", label: "APIs", scope: "permission:read", grantedBy: TENANT_ADMIN },
      {
        page: "applications",
        label: "Applications",
        scope: "application:read",
        grantedBy: TENANT_ADMIN,
      },
      { page: "projects", label: "Projects", scope: "project:list" },
      {
        page: "users",
        label: "Users",
        section: USER_MANAGEMENT,
        scope: "member:read",
        grantedBy: TENANT_OR_PROJECT_ADMIN,
      },
      {
        page: "roles",
        label: "Roles",
        section: USER_MANAGEMENT,
        scope: "role:read",
        grantedBy: TENANT_OR_PROJECT_ADMIN,
      },
      {
        page: "bulk",
        label: "Bulk add users",
        listed: false,
        scope: "member:invite",
        grantedBy: TENANT_ADMIN,
      },
      {
        page: "users-new",
        label: "New user",
        listed: false,
        scope: "member:invite",
        grantedBy: TENANT_ADMIN,
      },
      {
        page: "email",
        label: "Emails",
        section: USER_FLOWS,
        scope: "user-flow:read",
        grantedBy: TENANT_OR_PROJECT_ADMIN,
      },
      {
        page: "landing",
        label: "Landing pages",
        section: USER_FLOWS,
        scope: "user-flow:read",
        grantedBy: TENANT_OR_PROJECT_ADMIN,
      },
      { page: "claims", label: "Claims", scope: "claim:read", grantedBy: TENANT_ADMIN },
      { page: "logs", label: "Logs", scope: "audit:read", grantedBy: TENANT_OR_PROJECT_ADMIN },
    ],
  },
  {
    label: "Developer",
    pages: [{ page: "tokens", label: "Token inspector", href: "/tokens" }],
  },
];

export const PAGES: readonly PageDef[] = GROUPS.flatMap((group) => group.pages);

/**
 * What each page is called, by the last segment of its path.
 *
 * Here rather than in the sidebar: the name belongs to the page, and two surfaces read it — the
 * rail, which offers it, and the page's own header, which skips its title when the trail above
 * has already said the same word.
 */
export const LABELS: Record<string, string> = {
  ...Object.fromEntries(GROUPS.flatMap((group) => group.pages).map((e) => [e.page, e.label])),
  platform: "Tenants",
  tokens: "Token inspector",
  account: "Account",
};

/** The page's name, from a path's last segment. The segment itself when nothing claims it. */
export function labelOf(segment: string): string {
  return LABELS[segment] ?? segment;
}

/** The heading a page sits under, for the trail. `undefined` when it sits on its own. */
export function sectionOf(page: string): string | undefined {
  return PAGES.find((candidate) => candidate.page === page)?.section;
}

/**
 * The first page inside a tenant that this caller may actually open.
 *
 * So the place a tenant stands for is one its visitor can use. Sending everybody to the first
 * page in the table meant somebody who holds only `iam.member` was met by a refusal — which, now
 * that accepting an invitation signs them straight in, is the first thing they ever see.
 *
 * Falls back to the first page in the table when nothing matches: a caller who may open nothing
 * is owed the explanation `withTenant` already gives, not a blank screen.
 */
export function firstOpenPage(scopes: readonly string[]): Page {
  const inTenant = GROUPS.find((group) => group.tenant === true)?.pages ?? [];
  const open = inTenant.find(
    (candidate) =>
      candidate.listed !== false &&
      (candidate.scope === undefined || scopes.includes(candidate.scope)),
  );
  return open?.page ?? "apis";
}

export function pageDef(page: Page): PageDef {
  const found = PAGES.find((candidate) => candidate.page === page);
  if (found === undefined) throw new Error(`No page named ${page}`);
  return found;
}
